/**
 * useTour — хук управління онбордінг-туром кабінету.
 *
 * Версіонування: змініть TOUR_VERSION, щоб тур знову показався всім юзерам.
 */
import { useState, useCallback } from 'react';
import { NavTab } from '../components/layout/BottomNavbar';
import { UserPersona } from '../types/cabinet.types';

export const TOUR_VERSION = '1';
const STORAGE_KEY = 'cab_tour_version';

export type TourStepType =
  | { type: 'full' }                        // welcome step — no highlight
  | { type: 'selector'; selector: string }; // highlight DOM element by CSS selector

export interface TourStep {
  tab: NavTab;
  stepType: TourStepType;
  textKey: string; // key in TranslationKeys
}

/** Tour steps for Persona A (Otyłość) */
export const TOUR_STEPS_A: TourStep[] = [
  { tab: 'therapy',       stepType: { type: 'full' },                                               textKey: 'tour_welcome' },
  { tab: 'therapy',       stepType: { type: 'selector', selector: '[data-tour="bell-btn"]' },        textKey: 'tour_notif' },
  { tab: 'therapy',       stepType: { type: 'selector', selector: '[data-tour="control-card"]' },    textKey: 'tour_a1' },
  { tab: 'therapy',       stepType: { type: 'selector', selector: '[data-tour="visits-tile"]' },     textKey: 'tour_a2' },
  { tab: 'services',      stepType: { type: 'selector', selector: '[data-tour="nav-2"]' },           textKey: 'tour_a3' },
  { tab: 'documents',     stepType: { type: 'selector', selector: '[data-tour="nav-3"]' },           textKey: 'tour_a4' },
  { tab: 'profile',       stepType: { type: 'selector', selector: '[data-tour="nav-4"]' },           textKey: 'tour_a5' },
  { tab: 'profile',       stepType: { type: 'selector', selector: '[data-tour="pwa-banner"]' },      textKey: 'tour_pwa' },
];

/** Tour steps for Persona B and C */
export const TOUR_STEPS_BC: TourStep[] = [
  { tab: 'consultations', stepType: { type: 'full' },                                               textKey: 'tour_welcome' },
  { tab: 'consultations', stepType: { type: 'selector', selector: '[data-tour="bell-btn"]' },        textKey: 'tour_notif' },
  { tab: 'services',      stepType: { type: 'selector', selector: '[data-tour="nav-1"]' },           textKey: 'tour_b1' },
  { tab: 'consultations', stepType: { type: 'selector', selector: '[data-tour="nav-2"]' },           textKey: 'tour_b2' },
  { tab: 'documents',     stepType: { type: 'selector', selector: '[data-tour="nav-3"]' },           textKey: 'tour_b3' },
  { tab: 'profile',       stepType: { type: 'selector', selector: '[data-tour="nav-4"]' },           textKey: 'tour_b4' },
  { tab: 'profile',       stepType: { type: 'selector', selector: '[data-tour="pwa-banner"]' },      textKey: 'tour_pwa' },
];

export function isPwaMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const match = window.matchMedia('(display-mode: standalone)').matches;
    const navStandalone = ('standalone' in navigator) && (navigator as any).standalone;
    const localInstalled = localStorage.getItem('pwa_installed') === 'true';
    return match || navStandalone || localInstalled;
  } catch {
    return false;
  }
}

export function shouldShowTour(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== TOUR_VERSION;
  } catch {
    return false;
  }
}

export function markTourDone(): void {
  try {
    localStorage.setItem(STORAGE_KEY, TOUR_VERSION);
  } catch {
    // ignore storage errors
  }
}

export function resetTour(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface UseTourReturn {
  isActive: boolean;
  currentStep: number;
  steps: TourStep[];
  totalSteps: number;
  startTour: () => void;
  endTour: () => void;
  goNext: () => void;
  goPrev: () => void;
}

export function useTour(persona: UserPersona, isWeightLoss?: boolean): UseTourReturn {
  const isPersonaA = persona === 'A' || isWeightLoss === true;
  let steps = isPersonaA ? TOUR_STEPS_A : TOUR_STEPS_BC;

  // Do not show PWA installation step if already in PWA mode
  if (isPwaMode()) {
    steps = steps.filter(s => s.textKey !== 'tour_pwa');
  }

  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const endTour = useCallback(() => {
    setIsActive(false);
    markTourDone();
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep(prev => {
      const next = prev + 1;
      if (next >= steps.length) {
        setIsActive(false);
        markTourDone();
        return prev;
      }
      return next;
    });
  }, [steps.length]);

  const goPrev = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  return {
    isActive,
    currentStep,
    steps,
    totalSteps: steps.length,
    startTour,
    endTour,
    goNext,
    goPrev,
  };
}
